import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, Trash2, Loader2, Minus, Plus, Tag, Eye, EyeOff, ChevronDown, ChevronUp, Package } from "lucide-react";
import LessonArtwork from "@/components/LessonArtwork";
import { useCart } from "@/contexts/CartContext";
import ParcelLockerSelector from "@/components/ParcelLockerSelector";
import type { ParcelLocation } from "@/hooks/useParcelLocations";
import { getCourierLabel } from "@/hooks/useParcelLocations";
import type { TerminalData } from "@/hooks/useBookCheckout";
import { useAuth } from "@/contexts/AuthContext";
import { useBookCheckout, ShippingData } from "@/hooks/useBookCheckout";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import visaLogo from "@/assets/payment/visa.svg";
import mastercardLogo from "@/assets/payment/mastercard.png";
import googlePayLogo from "@/assets/payment/googlepay.png";
import applePayLogo from "@/assets/payment/applepay.png";
import payseraLogo from "@/assets/payment/paysera.png";
import { trackViewCart, trackBeginCheckout, trackAddPaymentInfo, cartItemToAnalytics } from "@/lib/analytics";
import UpsellRecommendations from "@/components/UpsellRecommendations";
import { EXPRESS_CHECKOUT_FLAG } from "@/lib/expressCheckoutFlag";

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface ShippingZone {
  name: string;
  countries: string[];
  price: number;
}

const COUNTRY_OPTIONS = [
  { code: "LT", label: "Lietuva" },
  { code: "LV", label: "Latvija" },
  { code: "EE", label: "Estija" },
  { code: "PL", label: "Lenkija" },
  { code: "DE", label: "Vokietija" },
  { code: "FR", label: "Prancūzija" },
  { code: "ES", label: "Ispanija" },
  { code: "IT", label: "Italija" },
  { code: "NL", label: "Nyderlandai" },
  { code: "BE", label: "Belgija" },
  { code: "AT", label: "Austrija" },
  { code: "FI", label: "Suomija" },
  { code: "SE", label: "Švedija" },
  { code: "DK", label: "Danija" },
  { code: "IE", label: "Airija" },
  { code: "PT", label: "Portugalija" },
  { code: "CZ", label: "Čekija" },
  { code: "SK", label: "Slovakija" },
  { code: "HU", label: "Vengrija" },
  { code: "RO", label: "Rumunija" },
  { code: "BG", label: "Bulgarija" },
  { code: "HR", label: "Kroatija" },
  { code: "SI", label: "Slovėnija" },
  { code: "GR", label: "Graikija" },
  { code: "GB", label: "Jungtinė Karalystė" },
  { code: "NO", label: "Norvegija" },
  { code: "CH", label: "Šveicarija" },
  { code: "US", label: "JAV" },
];

const COURIER_OPTIONS = [
  { code: "lp_express", label: "LP Express" },
];

const CartDrawer = ({ open, onClose }: CartDrawerProps) => {
  const {
    items: cartItems, removeItem, updateQuantity,
    total: cartTotal, discountedTotal: cartDiscountedTotal, count: cartCount,
    discount, discountLoading, discountError, applyDiscount, removeDiscount,
    expressItem,
  } = useCart();
  const { user } = useAuth();
  const { initiateCheckout, isLoading } = useBookCheckout();
  const isMobile = useIsMobile();

  /** Express checkout shows only the single digital product; the stored cart stays untouched. */
  const isExpress = !!expressItem;
  const items = useMemo(
    () => (expressItem ? [{ product: expressItem, quantity: 1 }] : cartItems),
    [expressItem, cartItems]
  );
  const total = isExpress ? items.reduce((s, i) => s + i.product.price * i.quantity, 0) : cartTotal;
  const count = isExpress ? 1 : cartCount;
  const discountedTotal = useMemo(() => {
    if (!isExpress) return cartDiscountedTotal;
    if (!discount) return total;
    const eligible = items.reduce((sum, i) => {
      const onSale = i.product.regularPrice != null && i.product.price < i.product.regularPrice;
      if (onSale) return sum;
      if (
        discount.appliesTo === "all" ||
        (discount.appliesTo === "books" && i.product.type === "book") ||
        (discount.appliesTo === "lessons" && i.product.type === "lesson")
      ) return sum + i.product.price * i.quantity;
      return sum;
    }, 0);
    const discounted = discount.type === "percentage"
      ? Math.max(0, eligible * (1 - discount.value / 100))
      : Math.max(0, eligible - discount.value);
    return (total - eligible) + discounted;
  }, [isExpress, cartDiscountedTotal, discount, items, total]);


  // Form state
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("LT");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState<"courier" | "address">("courier");
  const [selectedCourier, setSelectedCourier] = useState("lp_express");
  const [selectedTerminal, setSelectedTerminal] = useState<ParcelLocation | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"stripe" | "paysera" | "googlepay" | "applepay">("stripe");
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Shipping zones
  const [shippingZones, setShippingZones] = useState<ShippingZone[]>([]);

  useEffect(() => {
    if (!open) return;
    const fetchZones = async () => {
      const { data } = await supabase
        .from("store_settings")
        .select("value")
        .eq("key", "shipping")
        .maybeSingle();
      if (data?.value) {
        const val = data.value as any;
        setShippingZones(val.zones || []);
      }
    };
    fetchZones();
  }, [open]);

  // Escape closes the checkout (onClose also clears any express item)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll when checkout is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Track view_cart when drawer opens with items
    if (items.length > 0) {
      const analyticsItems = items.map(i => cartItemToAnalytics(i.product, i.quantity));
      const cartTotal = items.reduce((s, i) => s + i.product.price * i.quantity, 0);
      trackViewCart(analyticsItems, cartTotal);
    }
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Pre-fill email for logged-in users
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user]);

  const bookItems = items.filter((i) => i.product.type === "book");
  const lessonItems = items.filter((i) => i.product.type === "lesson");
  const zemelapisItems = items.filter((i) => i.product.type === "zemelapis");
  const hasPhysicalBooks = bookItems.length > 0;
  const hasLessons = lessonItems.length > 0;
  const hasZemelapis = zemelapisItems.length > 0;
  const isLoggedIn = !!user;

  // Force account creation for lessons or zemelapis (need user_id)
  useEffect(() => {
    if ((hasLessons || hasZemelapis) && !isLoggedIn) setCreateAccount(true);
  }, [hasLessons, hasZemelapis, isLoggedIn]);

  // Parcel-locker delivery is LT-only — outside Lithuania force address delivery
  useEffect(() => {
    if (country !== "LT" && deliveryMethod !== "address") {
      setDeliveryMethod("address");
      setSelectedTerminal(null);
    }
  }, [country, deliveryMethod]);




  // Shipping cost calculation — zones store English country names, map code→English name
  const CODE_TO_EN: Record<string, string> = {
    LT: "Lithuania", LV: "Latvia", EE: "Estonia", PL: "Poland", DE: "Germany",
    FR: "France", ES: "Spain", IT: "Italy", NL: "Netherlands", BE: "Belgium",
    AT: "Austria", FI: "Finland", SE: "Sweden", DK: "Denmark", IE: "Ireland",
    PT: "Portugal", CZ: "Czech Republic", SK: "Slovakia", HU: "Hungary", RO: "Romania",
    BG: "Bulgaria", HR: "Croatia", SI: "Slovenia", GR: "Greece", GB: "United Kingdom",
    NO: "Norway", CH: "Switzerland", US: "United States",
  };
  const shippingCost = useMemo(() => {
    if (!hasPhysicalBooks) return 0;
    const enName = CODE_TO_EN[country] || country;
    const zone = shippingZones.find(z => z.countries.includes(enName) || z.countries.includes(country));
    return zone?.price ?? 0;
  }, [country, shippingZones, hasPhysicalBooks]);

  const finalTotal = discountedTotal + shippingCost;

  if (!open) return null;

  // Validation
  const getValidationErrors = (): string[] => {
    const errors: string[] = [];
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) errors.push("Įveskite teisingą el. paštą");
    if (hasPhysicalBooks) {
      if (!name.trim()) errors.push("Įveskite vardą ir pavardę");
      if (!phone.trim()) errors.push("Įveskite telefono numerį");
      if (country === "LT" && deliveryMethod === "courier" && !selectedTerminal) errors.push("Pasirinkite paštomatą");
      if (country !== "LT" || deliveryMethod === "address") {
        if (!address.trim()) errors.push("Įveskite adresą");
        if (!city.trim()) errors.push("Įveskite miestą");
        if (!postalCode.trim()) errors.push("Įveskite pašto kodą");
      }
    }
    if (createAccount && !isLoggedIn && accountPassword.length < 8) errors.push("Slaptažodis per trumpas (min. 8 simboliai)");
    return errors;
  };

  const isFormValid = () => getValidationErrors().length === 0;

  const handleSubmit = async () => {
    const errors = getValidationErrors();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);

    const books = bookItems.flatMap(item =>
      Array.from({ length: item.quantity }, () => ({
        bookId: item.product.id,
        bookTitle: item.product.title,
        price: item.product.price,
      }))
    );
    const lessons = lessonItems.map(item => ({
      lessonId: item.product.id,
      lessonTitle: item.product.title,
      price: item.product.price,
    }));
    const zemelapis = zemelapisItems.flatMap(item =>
      Array.from({ length: item.quantity }, () => ({
        tier: (item.product.zemelapisTier || "single") as "single" | "gift_pair" | "gift_single" | "karjera" | "karjera_gift",
        senderName: item.product.giftSenderName,
        recipientName: item.product.giftRecipientName,
        recipientEmail: item.product.giftRecipientEmail,
        personalMessage: item.product.giftMessage,
      }))
    );

    const method = paymentMethod === "googlepay" || paymentMethod === "applepay" ? "stripe" : paymentMethod;

    const terminalData: TerminalData | undefined =
      deliveryMethod === "courier" && selectedTerminal
        ? {
            id: selectedTerminal.id,
            identifier: selectedTerminal.identifier,
            name: selectedTerminal.name,
            courier_code: selectedTerminal.courier_code,
            address: selectedTerminal.address,
            city: selectedTerminal.city,
            postal_code: selectedTerminal.postal_code,
          }
        : undefined;

    const shippingData: ShippingData | undefined =
      hasPhysicalBooks
        ? {
            name,
            address: deliveryMethod === "address" ? address : (selectedTerminal?.address || ""),
            city: deliveryMethod === "address" ? city : (selectedTerminal?.city || ""),
            postalCode: deliveryMethod === "address" ? postalCode : (selectedTerminal?.postal_code || ""),
            countryCode: country,
            phone,
            email,
          }
        : undefined;

    const needsAccountCreation = !isLoggedIn && createAccount;

    // An express purchase must never wipe the stored cart when the payment
    // provider redirects back — the return page reads this marker.
    try {
      if (isExpress) localStorage.setItem(EXPRESS_CHECKOUT_FLAG, String(Date.now()));
      else localStorage.removeItem(EXPRESS_CHECKOUT_FLAG);
    } catch { /* storage unavailable */ }

    const analyticsItems = items.map(i => cartItemToAnalytics(i.product, i.quantity));
    trackBeginCheckout(analyticsItems, finalTotal, discount?.code);
    trackAddPaymentInfo(method, finalTotal);

    await initiateCheckout({
      books,
      lessons,
      zemelapis,
      method,
      discountCode: discount?.code,
      shipping: shippingData,
      phone: hasPhysicalBooks ? phone : undefined,
      terminal: terminalData,
      shippingCost: hasPhysicalBooks ? shippingCost : undefined,
      createAccount: needsAccountCreation || undefined,
      accountEmail: needsAccountCreation ? email : undefined,
      accountPassword: needsAccountCreation ? accountPassword : undefined,
    });
  };


  // --- ORDER SUMMARY (right column / mobile collapsible) ---
  const orderSummary = (
    <div className="space-y-4">
      {/* Items */}
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.product.id} className="flex gap-3">
            <div className="w-14 h-[72px] flex-shrink-0 rounded-lg overflow-hidden bg-muted/30 border border-border">
              {item.product.type === "lesson" ? (
                <LessonArtwork title={item.product.title} seed={item.product.id} className="w-full h-full" />
              ) : item.product.cover_url ? (
                <img src={item.product.cover_url} alt={item.product.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                  📖
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground leading-tight line-clamp-2">{item.product.title}</p>
              {item.product.author && <p className="text-xs text-muted-foreground mt-0.5">{item.product.author}</p>}
              {!isExpress && item.product.type === "book" && (
                <div className="flex items-center gap-2 mt-1.5">
                  <button
                    onClick={() => item.quantity <= 1 ? removeItem(item.product.id) : updateQuantity(item.product.id, item.quantity - 1)}
                    className="w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground border border-border hover:bg-muted/50 transition-colors"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-xs font-medium text-foreground w-5 text-center">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                    className="w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground border border-border hover:bg-muted/50 transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className="text-sm font-semibold text-foreground">€{(item.product.price * item.quantity).toFixed(2)}</span>
              {!isExpress && (
                <button onClick={() => removeItem(item.product.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Discount */}
      <div className="border-t border-border pt-3 space-y-2">
        {discount ? (
          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-primary/10 border border-primary/20">
            <span className="flex items-center gap-1.5 text-xs text-primary font-semibold">
              <Tag className="w-3 h-3" />
              {discount.code} ({discount.type === "percentage" ? `${discount.value}%` : `€${discount.value}`})
            </span>
            <button onClick={removeDiscount} className="text-primary/60 hover:text-primary text-xs">✕</button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              placeholder="Nuolaidos kodas"
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
            <button
              onClick={() => applyDiscount(discountCode)}
              disabled={discountLoading || !discountCode.trim()}
              className="px-3 py-2 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors disabled:opacity-40"
            >
              {discountLoading ? "..." : "Taikyti"}
            </button>
          </div>
        )}
        {discountError && <p className="text-xs text-destructive">{discountError}</p>}
      </div>

      {/* Totals */}
      <div className="border-t border-border pt-3 space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Tarpinė suma</span>
          <span className="text-foreground">
            {discount && <span className="line-through text-muted-foreground mr-1.5">€{total.toFixed(2)}</span>}
            €{discountedTotal.toFixed(2)}
          </span>
        </div>
        {hasPhysicalBooks && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Pristatymas</span>
            <span className="text-foreground">{shippingCost > 0 ? `€${shippingCost.toFixed(2)}` : "—"}</span>
          </div>
        )}
        <div className="flex justify-between text-base font-bold pt-1.5 border-t border-border">
          <span className="text-foreground">Iš viso</span>
          <span className="text-foreground">€{finalTotal.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );

  // --- LEFT COLUMN ---
  const leftColumn = (
    <div className="space-y-6">
      {/* Contact */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">Kontaktai</h2>
        <div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="El. paštas *"
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>
      </section>

      {/* Shipping (physical books only) */}
      {hasPhysicalBooks && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">Pristatymas</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Vardas ir pavardė *"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              >
                {COUNTRY_OPTIONS.map(c => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="Pašto kodas *"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
            <div className="col-span-2">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Telefonas * (pvz. +370 600 00000)"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
          </div>
        </section>
      )}

      {/* Delivery method */}
      {hasPhysicalBooks && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">Pristatymo būdas</h2>
          <div className="space-y-2">
            {/* Courier options (only for LT) */}
            {country === "LT" && COURIER_OPTIONS.map(c => (
              <label
                key={c.code}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  deliveryMethod === "courier" && selectedCourier === c.code
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/30"
                }`}
                onClick={() => {
                  setDeliveryMethod("courier");
                  setSelectedCourier(c.code);
                  setSelectedTerminal(null);
                }}
              >
                <input
                  type="radio"
                  name="delivery"
                  checked={deliveryMethod === "courier" && selectedCourier === c.code}
                  onChange={() => {}}
                  className="accent-primary w-4 h-4"
                />
                <Package className="w-4 h-4 text-muted-foreground" />
                <span className="flex-1 text-sm text-foreground">{c.label}</span>
                <span className="text-sm font-medium text-foreground">€{shippingCost.toFixed(2)}</span>
              </label>
            ))}

            {/* Show parcel selector when courier selected */}
            {country === "LT" && deliveryMethod === "courier" && (
              <div className="pl-7">
                <ParcelLockerSelector
                  selected={selectedTerminal}
                  onSelect={setSelectedTerminal}
                  courierFilter={selectedCourier}
                />
              </div>
            )}

            {/* Address delivery */}
            <label
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                deliveryMethod === "address" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
              }`}
              onClick={() => { setDeliveryMethod("address"); setSelectedTerminal(null); }}
            >
              <input
                type="radio"
                name="delivery"
                checked={deliveryMethod === "address"}
                onChange={() => {}}
                className="accent-primary w-4 h-4"
              />
              <span className="flex-1 text-sm text-foreground">Pristatymas adresu</span>
              <span className="text-sm font-medium text-foreground">€{shippingCost.toFixed(2)}</span>
            </label>

            {deliveryMethod === "address" && (
              <div className="pl-7 space-y-2">
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Adresas *"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                />
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Miestas *"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Payment */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">Mokėjimas</h2>
        <div className="space-y-2">
          <label
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              paymentMethod === "stripe" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
            }`}
            onClick={() => setPaymentMethod("stripe")}
          >
            <input type="radio" name="payment" checked={paymentMethod === "stripe"} onChange={() => {}} className="accent-primary w-4 h-4" />
            <span className="flex-1 text-sm text-foreground">Mokėjimo kortelė</span>
            <div className="flex items-center gap-1.5">
              <img src={visaLogo} alt="Visa" className="h-5 w-auto opacity-70" />
              <img src={mastercardLogo} alt="Mastercard" className="h-5 w-auto opacity-70" />
            </div>
          </label>

          <label
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              paymentMethod === "paysera" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
            }`}
            onClick={() => setPaymentMethod("paysera")}
          >
            <input type="radio" name="payment" checked={paymentMethod === "paysera"} onChange={() => {}} className="accent-primary w-4 h-4" />
            <span className="flex-1 text-sm text-foreground">Paysera (bankai)</span>
            <img src={payseraLogo} alt="Paysera" className="h-5 w-auto opacity-70" />
          </label>

          <label
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              paymentMethod === "googlepay" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
            }`}
            onClick={() => setPaymentMethod("googlepay")}
          >
            <input type="radio" name="payment" checked={paymentMethod === "googlepay"} onChange={() => {}} className="accent-primary w-4 h-4" />
            <span className="flex-1 text-sm text-foreground">Google Pay</span>
            <img src={googlePayLogo} alt="Google Pay" className="h-5 w-auto opacity-70" />
          </label>

          <label
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              paymentMethod === "applepay" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
            }`}
            onClick={() => setPaymentMethod("applepay")}
          >
            <input type="radio" name="payment" checked={paymentMethod === "applepay"} onChange={() => {}} className="accent-primary w-4 h-4" />
            <span className="flex-1 text-sm text-foreground">Apple Pay</span>
            <img src={applePayLogo} alt="Apple Pay" className="h-5 w-auto opacity-70" />
          </label>
        </div>
      </section>

      {/* Terms */}
      <div className="space-y-3">
        {/* Create account */}
        {!isLoggedIn && (
          <div className="space-y-2">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={createAccount}
                onChange={(e) => !(hasLessons || hasZemelapis) && setCreateAccount(e.target.checked)}
                disabled={hasLessons || hasZemelapis}
                className="mt-0.5 h-4 w-4 accent-primary rounded"
              />
              <div>
                <span className="text-sm text-foreground font-medium">Sukurti paskyrą</span>
                {(hasLessons || hasZemelapis) ? (
                  <p className="text-xs text-primary/80 mt-0.5">Privaloma {hasLessons ? "pamokoms" : "žemėlapiui"}</p>

                ) : (
                  <p className="text-xs text-muted-foreground mt-0.5">Norint sekti užsakymus</p>
                )}
              </div>
            </label>
            {createAccount && (
              <div className="relative ml-6">
                <input
                  type={showPassword ? "text" : "password"}
                  value={accountPassword}
                  onChange={(e) => setAccountPassword(e.target.value)}
                  placeholder="Slaptažodis * (min. 8 simboliai)"
                  className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {accountPassword.length > 0 && accountPassword.length < 8 && (
                  <p className="text-xs text-destructive mt-1">Slaptažodis per trumpas</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 space-y-1">
          {validationErrors.map((err, i) => (
            <p key={i} className="text-xs text-destructive flex items-center gap-1.5">
              <span>•</span> {err}
            </p>
          ))}
        </div>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={isLoading}
        className="w-full py-3.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Kraunama...</>
        ) : (
          `Tęsti • €${finalTotal.toFixed(2)}`
        )}
      </button>

      <p className="text-xs text-muted-foreground text-center leading-relaxed">
        Spausdama „Tęsti" sutinki su{" "}
        <a href="/naudojimosi-salygos" target="_blank" rel="noopener noreferrer" className="text-primary underline">naudojimosi sąlygomis</a>
        {" "}ir{" "}
        <a href="/privatumo-politika" target="_blank" rel="noopener noreferrer" className="text-primary underline">privatumo politika</a>.
      </p>

      {bookItems.length > 0 && lessonItems.length > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          ⓘ Knygos ir pamokos apdorojamos atskirai.
        </p>
      )}
    </div>
  );

  // --- EMPTY CART ---
  if (items.length === 0) {
    return createPortal(
      <div className="fixed inset-0 z-[10000] bg-background/95 backdrop-blur-sm flex items-center justify-center overflow-hidden">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
          <X className="w-6 h-6" />
        </button>
        <div className="text-center space-y-3">
          <Package className="w-16 h-16 text-muted-foreground/30 mx-auto" />
          <p className="text-muted-foreground">Krepšelis tuščias</p>
          <button onClick={onClose} className="text-sm text-primary underline">Grįžti į parduotuvę</button>
        </div>
      </div>,
      document.body
    );
  }

  // --- FULL SCREEN CHECKOUT ---
  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-background overflow-y-auto overflow-x-hidden">
      {/* Close button - desktop only */}
      {!isMobile && (
        <button
          onClick={onClose}
          className="fixed top-4 right-4 z-10 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      )}

      <div className="max-w-5xl w-full mx-auto px-4 py-8">
        {/* Mobile: always-expanded order summary + close button */}
        {isMobile && (
          <div className="mb-6 border border-border rounded-lg">
            <div className="flex items-center">
              <div className="flex-1 flex items-center justify-between px-4 py-3 text-sm font-medium text-foreground">
                <span>Užsakymo suvestinė ({count})</span>
                <span className="font-bold">€{finalTotal.toFixed(2)}</span>
              </div>
              <button
                onClick={onClose}
                className="p-2 mr-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-4 pb-4">{orderSummary}</div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Left column */}
          <div className="lg:col-span-3">{leftColumn}</div>

          {/* Right column (desktop only) */}
          {!isMobile && (
            <div className="lg:col-span-2">
              <div className="sticky top-8 border border-border rounded-lg p-5">
                <h2 className="text-base font-semibold text-foreground mb-4">Užsakymo suvestinė</h2>
                {orderSummary}
              </div>
            </div>
          )}
        </div>

        {!isExpress && <UpsellRecommendations context="cart" className="mt-10" />}
      </div>
    </div>,
    document.body
  );
};

export default CartDrawer;
